import { useEffect, useMemo } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";

const SPRING = { stiffness: 75, damping: 20, mass: 1 };

function parse(value) {
  if (typeof value !== "string") return null;
  const match = value.match(/^([+-]?\d+(?:\.\d+)?)(K|M|%)?$/);
  if (!match) return null;
  const num = parseFloat(match[1]);
  const suffix = match[2] || "";
  const decimals = match[1].includes(".") ? match[1].split(".")[1].length : 0;
  return { num, suffix, decimals };
}

// Accepts already-formatted strings like "42", "1.2K", "82.0%", "0.00"
// Non-numeric strings ("—", "AI news", "0/1", "00:00") render as-is.
export function AnimatedValue({ value, className }) {
  const parsed = useMemo(() => parse(value), [value]);

  const motionVal = useMotionValue(0);
  const spring = useSpring(motionVal, SPRING);
  const displayed = useTransform(spring, (v) => {
    if (!parsed) return value;
    const clamped = Math.max(0, v);
    const n = parsed.decimals > 0
      ? clamped.toFixed(parsed.decimals)
      : String(Math.round(clamped));
    return n + parsed.suffix;
  });

  useEffect(() => {
    motionVal.set(parsed ? parsed.num : 0);
  }, [parsed?.num]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!parsed) {
    return className ? <span className={className}>{value}</span> : <>{value}</>;
  }

  return <motion.span className={className}>{displayed}</motion.span>;
}
